import React, { useState, useRef, useEffect } from 'react';
import { View, Text, TouchableOpacity, StyleSheet, Platform, TextInput, Animated, Easing, Alert } from 'react-native';
import LoadingSpinner from '../components/LoadingSpinner';
import { ScreenBackground } from '../components/ThemeGradients';
import { Ionicons } from '@expo/vector-icons';
import { BlurView } from 'expo-blur';
import Svg, { Path, LinearGradient as SvgLinearGradient, Defs, Stop, G, ClipPath, Rect } from 'react-native-svg';
import { useNavigation } from '../context/NavigationContext';
import GradientText from '../components/GradientText';

export const Input = ({ placeholder, value, onChangeText, keyboardType = 'default', secureTextEntry = false, disabled = false, placeholderTextColor = "rgba(255, 255, 255, 0.40)" }) => {
    const [isFocused, setIsFocused] = useState(false);
    const focusT = useRef(new Animated.Value(0)).current;
    const onFocus = () => {
        setIsFocused(true);
        Animated.timing(focusT, { toValue: 1, duration: 1500, easing: Easing.bezier(0.22, 1, 0.36, 1), useNativeDriver: true }).start();
    };
    const onBlur = () => {
        setIsFocused(false);
        Animated.timing(focusT, { toValue: 0, duration: 1500, easing: Easing.bezier(0.22, 1, 0.36, 1), useNativeDriver: true }).start();
    };
    return (
        <View style={[styles.inputContainer, isFocused && { borderColor: 'rgba(255, 255, 255, 0.9)' }]}>
            <TextInput
                placeholder={placeholder}
                placeholderTextColor={placeholderTextColor}
                style={styles.input}
                value={value}
                onChangeText={onChangeText}
                keyboardType={keyboardType}
                secureTextEntry={secureTextEntry}
                onFocus={onFocus}
                onBlur={onBlur}
                editable={!disabled}
                autoCapitalize="none"
            />
        </View>
    );
};

export default function ResetPasswordScreen() {
    const { navigate } = useNavigation();
    const [password, setPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [token, setToken] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);

    console.log('[ResetPasswordScreen] Component mounted/rendered');

    // Extract token from URL on mount
    useEffect(() => {
        console.log('[ResetPasswordScreen] useEffect running - Platform:', Platform.OS);
        if (Platform.OS === 'web') {
            const params = new URLSearchParams(window.location.search);
            const tokenParam = params.get('token');
            console.log('[ResetPasswordScreen] Token from URL:', tokenParam);
            if (tokenParam) {
                setToken(tokenParam);
            } else {
                setError('Invalid or missing reset token');
            }
        }
    }, []);

    const handleResetPassword = async () => {
        setError('');

        // Validation
        if (!password) {
            setError('Please enter a password');
            return;
        }
        if (password.length < 6) {
            setError('Password must be at least 6 characters');
            return;
        }
        if (password !== confirmPassword) {
            setError('Passwords do not match');
            return;
        }
        if (!token) {
            setError('Invalid reset token');
            return;
        }

        setLoading(true);

        try {
            // Call the backend endpoint
            const response = await fetch('https://us-central1-bet-optimized-fe922.cloudfunctions.net/processPasswordReset', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    token,
                    password,
                }),
            });

            const data = await response.json();

            if (!response.ok) {
                throw new Error(data.error || 'Failed to reset password');
            }

            setSuccess(true);
            // Redirect to login after 2 seconds
            setTimeout(() => {
                navigate('Login');
            }, 2000);

        } catch (err) {
            console.error('Reset password error:', err);
            setError(err.message || 'An error occurred. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    if (success) {
        return (
            <View style={styles.container}>
                <ScreenBackground />
                <View style={styles.gradient}>
                    <View style={styles.centerContainer}>
                        <View style={styles.successContainer}>
                            <Ionicons name="checkmark-circle" size={64} color="#fbbf24" />
                            <Text style={styles.successTitle}>Password Reset Successfully!</Text>
                            <Text style={styles.successText}>
                                Redirecting you to login...
                            </Text>
                        </View>
                    </View>
                </View>
            </View>
        );
    }

    return (
        <View style={styles.container}>
            <ScreenBackground />
            <View style={styles.gradient}>
                {/* Back button */}
                <TouchableOpacity
                    style={styles.backButton}
                    onPress={() => navigate('Login')}
                >
                    <Ionicons name="arrow-back" size={24} color="#fff" />
                </TouchableOpacity>

                <View style={styles.centerContainer}>
                    <View style={styles.formContainer}>
                        {/* Title */}
                        <View style={styles.titleContainer}>
                            <GradientText style={styles.title}>
                                Reset Password
                            </GradientText>
                            <Text style={styles.subtitle}>
                                Enter your new password below
                            </Text>
                        </View>

                        {/* Error message */}
                        {error ? (
                            <View style={styles.errorContainer}>
                                <Ionicons name="alert-circle" size={20} color="#ef4444" />
                                <Text style={styles.errorText}>{error}</Text>
                            </View>
                        ) : null}

                        {/* Password input */}
                        <View style={styles.inputWrapper}>
                            <Text style={styles.inputLabel}>New Password</Text>
                            <View style={styles.passwordInputContainer}>
                                <Input
                                    placeholder="Enter new password"
                                    value={password}
                                    onChangeText={setPassword}
                                    secureTextEntry={!showPassword}
                                    disabled={loading || !token}
                                />
                                <TouchableOpacity
                                    style={styles.eyeIcon}
                                    onPress={() => setShowPassword(!showPassword)}
                                >
                                    <Ionicons
                                        name={showPassword ? "eye-off" : "eye"}
                                        size={20}
                                        color="rgba(255, 255, 255, 0.5)"
                                    />
                                </TouchableOpacity>
                            </View>
                        </View>

                        {/* Confirm Password input */}
                        <View style={styles.inputWrapper}>
                            <Text style={styles.inputLabel}>Confirm Password</Text>
                            <View style={styles.passwordInputContainer}>
                                <Input
                                    placeholder="Confirm new password"
                                    value={confirmPassword}
                                    onChangeText={setConfirmPassword}
                                    secureTextEntry={!showConfirmPassword}
                                    disabled={loading || !token}
                                />
                                <TouchableOpacity
                                    style={styles.eyeIcon}
                                    onPress={() => setShowConfirmPassword(!showConfirmPassword)}
                                >
                                    <Ionicons
                                        name={showConfirmPassword ? "eye-off" : "eye"}
                                        size={20}
                                        color="rgba(255, 255, 255, 0.5)"
                                    />
                                </TouchableOpacity>
                            </View>
                        </View>

                        {/* Submit button */}
                        <TouchableOpacity
                            style={[
                                styles.submitButton,
                                (loading || !token) && styles.submitButtonDisabled
                            ]}
                            onPress={handleResetPassword}
                            disabled={loading || !token}
                        >
                            {loading ? (
                                <LoadingSpinner size="small" />
                            ) : (
                                <Text style={styles.submitButtonText}>Reset Password</Text>
                            )}
                        </TouchableOpacity>

                        {/* Back to login link */}
                        <TouchableOpacity
                            style={styles.loginLink}
                            onPress={() => navigate('Login')}
                        >
                            <Text style={styles.loginLinkText}>
                                Back to Login
                            </Text>
                        </TouchableOpacity>
                    </View>
                </View>
            </View>
        </View>
    );
}

const styles = StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: '#000000',
    },
    gradient: {
        flex: 1,
    },
    backButton: {
        position: 'absolute',
        top: Platform.OS === 'web' ? 32 : 60,
        left: 24,
        zIndex: 10,
        padding: 8,
    },
    centerContainer: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
        padding: 24,
    },
    formContainer: {
        width: '100%',
        maxWidth: 440,
    },
    titleContainer: {
        marginBottom: 32,
    },
    title: {
        fontSize: 32,
        fontWeight: '800',
        color: '#fff',
        marginBottom: 8,
        textAlign: 'center',
    },
    subtitle: {
        fontSize: 16,
        color: 'rgba(255, 255, 255, 0.6)',
        textAlign: 'center',
    },
    errorContainer: {
        flexDirection: 'row',
        alignItems: 'center',
        backgroundColor: 'rgba(239, 68, 68, 0.1)',
        borderWidth: 1,
        borderColor: 'rgba(239, 68, 68, 0.3)',
        borderRadius: 12,
        padding: 12,
        marginBottom: 24,
    },
    errorText: {
        color: '#ef4444',
        fontSize: 14,
        marginLeft: 8,
        flex: 1,
    },
    inputWrapper: {
        marginBottom: 20,
    },
    inputLabel: {
        fontSize: 14,
        fontWeight: '600',
        color: 'rgba(255, 255, 255, 0.8)',
        marginBottom: 8,
    },
    passwordInputContainer: {
        position: 'relative',
    },
    eyeIcon: {
        position: 'absolute',
        right: 16,
        top: 16,
        zIndex: 10,
    },
    inputContainer: {
        backgroundColor: 'rgba(255, 255, 255, 0.05)',
        borderRadius: 12,
        borderWidth: 1,
        borderColor: 'rgba(255, 255, 255, 0.1)',
        paddingHorizontal: 16,
        paddingVertical: 12,
    },
    input: {
        color: '#fff',
        fontSize: 16,
        paddingRight: 40, // Space for eye icon
    },
    submitButton: {
        backgroundColor: '#fbbf24',
        borderRadius: 12,
        paddingVertical: 16,
        alignItems: 'center',
        justifyContent: 'center',
        marginTop: 8,
        minHeight: 52,
    },
    submitButtonDisabled: {
        opacity: 0.5,
    },
    submitButtonText: {
        color: '#0f0f17',
        fontSize: 16,
        fontWeight: '700',
        letterSpacing: 0.5,
    },
    loginLink: {
        marginTop: 24,
        alignItems: 'center',
    },
    loginLinkText: {
        color: '#fbbf24',
        fontSize: 14,
        fontWeight: '600',
    },
    successContainer: {
        alignItems: 'center',
        backgroundColor: 'rgba(255, 255, 255, 0.05)',
        borderRadius: 16,
        padding: 40,
    },
    successTitle: {
        fontSize: 24,
        fontWeight: '700',
        color: '#fff',
        marginTop: 16,
        marginBottom: 8,
        textAlign: 'center',
    },
    successText: {
        fontSize: 16,
        color: 'rgba(255, 255, 255, 0.6)',
        textAlign: 'center',
    },
});
